/**
 * CaseCitationCard — the citation itself, on the case page.
 *
 * Lloyd, Aug 19 2026: "in reality, if the citation is readily visible, that has all the information
 * I really ever need." It was never shown. The scan has always been filed as a document, so seeing
 * it meant opening the Documents tab, finding the right file and opening it in another window —
 * three steps to look at the one piece of paper the whole case is about.
 *
 * ## Which file it shows, and why it says so
 *
 * `Ticket_Url` holds the document guid of the scan uploaded at intake, and is the answer whenever
 * it is set. It is empty on most existing cases: imported Clio cases were created without it and
 * the public intake materialiser leaves it blank, so on those the card falls back to the case's own
 * documents — a name that looks like a citation first, then the first image or PDF filed on the
 * case.
 *
 * That fallback is a guess, so the card always names the file it is showing and offers the
 * Documents tab beside it. A confidently displayed wrong document on a case page is worse than an
 * empty box: somebody would read a court date off it.
 *
 * Fetched as a blob with the session cookie rather than pointed at with a bare `src`, so it renders
 * the same whether the API is same-origin or behind API_HOST in development.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import UserDocumentApi, { UserDocumentDto } from '../../api/UserDocumentApi';

interface CaseCitationCardProps {
  User_guid: string;
  Ticket_guid: string;
  /** The document guid recorded on the case at intake. Empty on imported and public-intake cases. */
  Ticket_Url: string;
  /** Takes the user to the Documents tab, for when the guess is wrong or nothing is filed. */
  onOpenDocuments?: () => void;
}

function isPdf(name: string): boolean {
  return name.toLowerCase().endsWith('.pdf');
}

function looksLikeACitation(name: string): boolean {
  return /citation|ticket/i.test(name);
}

/**
 * The document to show, and how sure we are.
 *
 * 'recorded' means the case itself points at this file. 'guess' means nothing did, and this is the
 * likeliest of what is filed — which the card says out loud.
 */
function pickCitation(
  documents: UserDocumentDto[],
  ticketUrl: string,
): { document: UserDocumentDto; source: 'recorded' | 'guess' } | null {
  const viewable = documents.filter(
    (doc) =>
      UserDocumentApi.isImageFile(doc.UserDocument_Name) ||
      isPdf(doc.UserDocument_Name),
  );

  const recorded = ticketUrl
    ? viewable.find((doc) => doc.UserDocument_guid === ticketUrl)
    : undefined;
  if (recorded) return { document: recorded, source: 'recorded' };

  const named = viewable.find((doc) => looksLikeACitation(doc.UserDocument_Name));
  if (named) return { document: named, source: 'guess' };

  return viewable.length > 0 ? { document: viewable[0], source: 'guess' } : null;
}

function CaseCitationCard({
  User_guid,
  Ticket_guid,
  Ticket_Url,
  onOpenDocuments,
}: CaseCitationCardProps) {
  const [documents, setDocuments] = useState<UserDocumentDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  // Revoked on unmount and whenever a different file loads — these hold the whole scan in memory.
  const blobRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    UserDocumentApi.getUserDocuments(User_guid, Ticket_guid)
      .then((docs) => {
        if (!cancelled) setDocuments(docs);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Could not load the case documents.',
          );
          setDocuments([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [User_guid, Ticket_guid]);

  const picked = useMemo(
    () => (documents ? pickCitation(documents, Ticket_Url) : null),
    [documents, Ticket_Url],
  );

  const releaseBlob = useCallback(() => {
    if (blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!picked) return;
    let cancelled = false;
    const url = UserDocumentApi.getManagerDocumentUrl(
      User_guid,
      picked.document.UserDocument_guid,
    );

    fetch(url, { credentials: 'include' })
      .then((response) => {
        if (!response.ok) throw new Error('Could not open the citation.');
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        releaseBlob();
        const objectUrl = URL.createObjectURL(blob);
        blobRef.current = objectUrl;
        setBlobUrl(objectUrl);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not open the citation.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [picked, User_guid, releaseBlob]);

  useEffect(() => releaseBlob, [releaseBlob]);

  const documentsLink = onOpenDocuments ? (
    <Link
      component="button"
      type="button"
      variant="caption"
      underline="hover"
      onClick={onOpenDocuments}
    >
      Open the Documents tab
    </Link>
  ) : null;

  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          mb={1}
        >
          <Typography variant="h6">Citation</Typography>
          {picked && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Link
                component="button"
                type="button"
                variant="caption"
                underline="hover"
                onClick={() => setExpanded((open) => !open)}
              >
                {expanded ? 'Show smaller' : 'Show full size'}
              </Link>
              {blobUrl && (
                <Link
                  href={blobUrl}
                  target="_blank"
                  rel="noopener"
                  variant="caption"
                  underline="hover"
                >
                  Open in a new tab
                </Link>
              )}
            </Stack>
          )}
        </Stack>

        {documents === null ? (
          <Typography variant="body2" color="text.secondary">
            Looking for the citation…
          </Typography>
        ) : !picked ? (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              No scan of the citation is filed on this case yet.
            </Typography>
            {onOpenDocuments && (
              <Box>
                <Button size="small" variant="outlined" onClick={onOpenDocuments}>
                  Attach it on the Documents tab
                </Button>
              </Box>
            )}
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              {picked.source === 'recorded'
                ? `Showing ${picked.document.UserDocument_Name}, the scan filed with this case.`
                : `No scan is recorded against this case, so this is the likeliest of its documents: ${picked.document.UserDocument_Name}.`}{' '}
              {documentsLink}
            </Typography>

            {error ? (
              <Alert severity="warning" variant="outlined">
                {error}
              </Alert>
            ) : !blobUrl ? (
              <Typography variant="body2" color="text.secondary">
                Loading…
              </Typography>
            ) : isPdf(picked.document.UserDocument_Name) ? (
              <Box
                component="iframe"
                title="Citation"
                src={blobUrl}
                sx={{
                  width: '100%',
                  height: expanded ? 900 : 420,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              />
            ) : (
              <Box
                component="img"
                alt="Citation"
                src={blobUrl}
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: expanded ? 'none' : 420,
                  objectFit: 'contain',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              />
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default CaseCitationCard;
